(ns graph-ui.web.activity
  "Client-side activity ring-buffer backing the :activity-log observability widget
   — PLAN-graph-ui-v0-006-01, dogfood debugging.

   WHY CLIENT-SIDE: the log has to show BOTH bus commands (with round-trip ms) AND
   the UI-only events that never reach the bus — zoom-to-fit, widget select, the
   island-view actually painting. A server source could never see the latter. So,
   exactly like graph-ui.web.draft, this is a reagent atom read DIRECTLY by the
   widget (never a bus :source): it adds no server load, needs no :bind, and keeps
   its history even while the thing it observes is stuck — which is the whole point
   when the island-select cascade hangs for 30-40s.

   One entry = {:kind :dispatch|:event|:ui, :at <epoch-ms>, ...}. `record!` stamps
   :at and trims to the last CAP; `entries` is the reactive read; `clear!` resets.

   Fed from three places, all via record!:
   - graph-ui.web.bus-client — every :dispatch reply records {:kind :dispatch :type :ms}.
   - graph-ui.web.island-view — zoom changes and widget selects record {:kind :ui …}.
   - (future) server-side per-handler timing, if finer attribution than round-trip
     ms is needed for the perf hunt (page-outline tree-build); round-trip already
     names the slow COMMAND, which is enough to dogfood."
  (:require [reagent.core :as r]
            [clojure.string :as string]))

;; @feature F-graph-ui-v0-001-01-ui-quality-system
;; @concept CONCEPT-ikc-v3-003-01-observability-surface

;; Newest-last vector so the widget can render top-to-bottom chronologically and
;; scroll to the tail. Capped so a long session can't grow it without bound —
;; this is a debugging tail, not an audit journal (that's ADR-ikc-v3-035-01).
(defonce ^:private log (r/atom []))
(def ^:private cap 200)

(defn record!
  ;; CONCEPT: CONCEPT-ikc-v3-003-01-observability-surface
  "Append one activity entry (a map; :kind is :dispatch|:event|:ui). Stamps :at
   with the wall clock and trims the buffer to the last `cap` entries. Safe to
   call from any client code path — it only touches this atom."
  [entry]
  (let [e (assoc entry :at (js/Date.now))]
    (swap! log (fn [v]
                 (let [v' (conj v e)
                       n  (count v')]
                   (if (> n cap) (subvec v' (- n cap)) v'))))))

(defn entries
  ;; CONCEPT: CONCEPT-ikc-v3-003-01-observability-surface
  "Reactive read of the activity buffer (oldest first). Deref inside a reagent
   render re-renders it as new activity arrives."
  []
  @log)

(defn clear!
  "Empty the buffer — the widget's Clear affordance."
  []
  (reset! log []))

(defn- fmt-line
  "One entry → a plain-text line (HH:MM:SS.mmm + kind-specific body), the shape
   the widget renders and Copy-all yields."
  [{:keys [kind at type ms label detail]}]
  (let [d  (js/Date. at)
        p2 #(.padStart (str %) 2 "0")
        p3 #(.padStart (str %) 3 "0")
        t  (str (p2 (.getHours d)) ":" (p2 (.getMinutes d)) ":" (p2 (.getSeconds d))
                "." (p3 (.getMilliseconds d)))]
    (case kind
      :dispatch (str t "  -> " type (when detail (str " " detail)) "  " ms "ms")
      :ui       (str t "  * " label (when detail (str " " detail)))
      :event    (str t "  <- " type)
      (str t "  " (pr-str kind)))))

(defn as-text
  "The whole buffer as newline-joined plain text — what the widget's Copy-all
   button writes to the clipboard, so a dogfood cascade can be pasted verbatim."
  []
  (string/join "\n" (map fmt-line @log)))
